import { Check, ArrowRight, Star } from "lucide-react";
import VatInfoTooltip from "../ui/VatInfoTooltip";

const CsomagokSection = ({ onOpenModal, onOpenDetails, onOpenProDetails }) => {
  const premiumFeatures = [
    "Teljes weboldal szövegezés (headline-ok, alcímek, CTA-k, microcopy)",
    "Konverziós felépítés: szekciók logikája + kifogáskezelő GYIK",
    "Szolgáltatás szekció kártyákkal (érthetően, gyorsan átláthatóan)",
    "3 lépéses folyamat blokk (bizalom és kiszámíthatóság)",
    "Kattintható CTA-k: gombok, sticky CTA mobilon",
    "Google térkép beágyazás (ha van cím / terület)",
    "Képgaléria / referencia blokk (ha adsz anyagot)",
    "YouTube videó beágyazás (ha van videód — opcionális)",
    "Alap analitika mérés (alap szintű látogatottsági mérés beállítás)",
    "Átadás után kérhető gyors módosítások (egyeztetés szerint)"
  ];

  const proFeatures = [
    "Professzionális SEO csomag (kulcsszó-kutatással)",
    "Ajánlatkérő / kapcsolati űrlap PRO",
    "Social media csomag: Facebook / Google / Tiktok pixel elhelyezés + konverziókövetés"
  ];

  const customExamples = [
    "Speciális űrlap-logika / több lépcsős lead gyűjtés",
    "Több aloldal / több célcsoport",
    "Egyedi integrációk (időpontfoglaló, külső eszközök)",
    "Extra szekciók (esettanulmány, összehasonlítás, 'miért mi' mély blokk)",
    "Foglalási rendszer integráció (Calendly / Booked / stb)",
    "Multinyelvű verzió (HU/EN) vagy több lokációs landing"
  ];

  return (
    <section id="csomagok" data-testid="csomagok-section" className="light-band-2 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div>
          <h2 className="scroll-animate font-sora font-bold text-3xl sm:text-4xl lg:text-5xl text-ink-text text-center">
            Csomagok — <span className="text-primary">átláthatóan</span>
          </h2>

          {/* Desktop: 2 columns top, full width bottom | Mobile: stacked */}
          <div className="scroll-animate-stagger scroll-animate mt-12 flex flex-col gap-6">
            {/* Top row: Premium + PRO packages */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Premium package */}
              <div className="card-light rounded-3xl p-8 md:p-10 flex flex-col">
                <h3 className="font-sora font-bold text-2xl text-ink-text">Prémium Landing csomag</h3>
                <div className="mt-4 flex items-baseline">
                  <span className="text-2xl font-bold text-ink-text">97 000</span>
                  <span className="text-xl text-muted-text ml-2">Ft</span>
                  <VatInfoTooltip />
                </div>
                <p className="mt-4 text-muted-text">
                  Egy modern, prémium landing oldal szolgáltatóknak — ügyfélszerzésre építve.
                </p>

                <div className="mt-8 space-y-3">
                  {premiumFeatures.slice(0, 5).map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={18} className="text-accent mt-0.5 flex-shrink-0" />
                      <span className="text-ink-text/80 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={onOpenDetails}
                  className="mt-4 text-primary text-sm font-medium hover:underline"
                  data-testid="package-details-btn"
                >
                  Mit tartalmaz pontosan? →
                </button>

                <div className="mt-auto pt-8">
                  <button
                    onClick={onOpenModal}
                    className="w-full bg-ink-text hover:bg-ink-text-2 text-white rounded-full py-4 font-semibold transition-colors"
                    data-testid="premium-package-cta"
                  >
                    Ezt kérem
                  </button>
                </div>
              </div>

              {/* PRO SEO & Lead package - Featured */}
              <div className="pricing-featured rounded-3xl p-8 md:p-10 relative overflow-hidden flex flex-col">
                <span className="inline-block mb-4 px-4 py-1.5 rounded-full bg-primary text-white text-xs font-semibold">
                  Kiemelt ajánlat
                </span>
                
                <h3 className="font-sora font-bold text-2xl text-ink-text mt-0">PRO SEO &amp; Lead csomag</h3>
                <div className="mt-4 flex items-baseline">
                  <span className="text-2xl font-bold text-ink-text">167 000</span>
                  <span className="text-xl text-muted-text ml-2">Ft</span>
                  <VatInfoTooltip />
                </div>
                <p className="mt-4 text-muted-text">
                  Profi SEO kulcsszó-kutatással, és komoly ajánlatkérő rendszerrel. Akkor válaszd, ha nem csak jelenlét kell, hanem kereshető, mérhető és lead-re optimalizált oldal.
                </p>

                {/* Highlighted "Everything in Premium +" */}
                <div className="mt-6 p-3 rounded-xl bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30">
                  <div className="flex items-center gap-2">
                    <Star size={16} className="text-accent fill-accent" />
                    <span className="font-semibold text-ink-text text-sm">Minden ami a Prémium csomagban +</span>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  {proFeatures.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={18} className="text-accent mt-0.5 flex-shrink-0" />
                      <span className="text-ink-text/80 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={onOpenProDetails}
                  className="mt-4 text-primary text-sm font-medium hover:underline"
                  data-testid="pro-package-details-btn"
                >
                  Mit tartalmaz pontosan? →
                </button>

                <div className="mt-auto pt-8">
                  <button
                    onClick={onOpenModal}
                    className="w-full bg-gradient-to-r from-primary to-accent text-white rounded-full py-4 font-semibold shadow-glow hover:shadow-glow-lg transition-all btn-glow"
                    data-testid="pro-package-cta"
                  >
                    Ezt kérem
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom row: Custom package - full width on desktop */}
            <div className="card-light rounded-3xl p-6 md:p-8 lg:p-6 flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1">
                <h3 className="font-sora font-bold text-xl lg:text-2xl text-ink-text">Egyedi ajánlat</h3>
                <p className="mt-2 text-muted-text text-sm lg:text-base">
                  Ha extra igényed van (pl. több oldal, speciális blokkok, integrációk), megbeszéljük és adunk rá ajánlatot.
                </p>
              </div>

              <div className="flex-1 lg:flex-[2]">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {customExamples.map((example, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <ArrowRight size={14} className="text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-ink-text/80 text-xs">{example}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:flex-shrink-0">
                <button
                  onClick={onOpenModal}
                  className="w-full lg:w-auto bg-ink-text hover:bg-ink-text-2 text-white rounded-full px-8 py-3 font-semibold transition-colors whitespace-nowrap"
                  data-testid="custom-package-cta"
                >
                  Egyedi ajánlatot kérek
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CsomagokSection;
